/**
 * Conditional-GET polling for one `web.*` view.
 *
 * WHY POLLING AND NOT A SOCKET. Staleness here is self-healing and low-stakes: the worst
 * case is an admin looking at a home that was renamed nine seconds ago. A websocket would
 * mean a stateful connection through the Worker, a fan-out story on the sidecar, and a
 * reconnect path - three new failure modes to make a read-only inspector nine seconds
 * fresher. Ten seconds plus refetch-on-focus is the right amount of machinery.
 *
 * WHAT MAKES IT CHEAP. Every poll carries the previous ETag, and the sidecar answers 304
 * with no body when nothing changed (PostgREST itself emits no ETag - the sidecar hashes
 * the response, see backend/src/dbproxy.ts). A player detail page fans out over eight
 * views; without the 304 path that is eight full bodies every ten seconds, per open tab.
 *
 * REFETCH ON FOCUS is what makes the interval feel shorter than it is: an admin who
 * alt-tabs back to the tab gets fresh data on the same tick they look at it, and a
 * backgrounded tab does not need to poll at all.
 *
 * A 404 STOPS THE POLL. Every other failure is a blip worth retrying - the sidecar restarting
 * (502), a token expiring (401), the rate limit (429) - but a 404 means the VIEW IS NOT THERE:
 * PostgREST answers `42P01 relation "web_self.player_homes" does not exist` when the mod's
 * migrations, or the second web-roles.sql pass, have never run against this Postgres (and the
 * sidecar answers `unknown_view` for a name it does not serve). No number of retries creates a
 * view, so the hook says `missing` and asks nothing more until a reload or a `refresh()`.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { WebViewName, WebViewRow } from '@dimensionscore/shared';

import { ApiError, type ApiClient, type Cached } from './client';

/** The inspector's refresh cadence. */
export const POLL_INTERVAL_MS = 10_000;

export type ViewStatus = 'disabled' | 'loading' | 'ready' | 'error';

export interface PolledView<T> {
  readonly rows: readonly T[] | null;
  readonly status: ViewStatus;
  readonly error: string | null;
  /**
   * True when the last answer was a 404: the view does not exist on this database, so polling has
   * stopped. `status` is `error` as well, so a page that does not care keeps showing a failure.
   */
  readonly missing: boolean;
  /** Epoch millis of the last answer, 304s included - it is a freshness proof too. */
  readonly checkedAt: number | null;
  /** Answers that carried a new body. Distinguishing the two is what the tests assert. */
  readonly fetchCount: number;
  /** Answers that were 304. High is good: it means the poll is costing almost nothing. */
  readonly notModifiedCount: number;
  /** Forces a poll now; used by the manual refresh control and by focus handling. */
  readonly refresh: () => void;
}

export interface UsePolledViewOptions {
  /** False parks the hook: no request, no timer. Used while signed out. */
  readonly enabled?: boolean;
  readonly intervalMs?: number;
}

/**
 * @param query PostgREST filters. Must be a stable value or a literal - it is compared by
 *              serialised content, so an inline object is fine and will not spin.
 */
export function usePolledView<N extends WebViewName>(
  client: ApiClient,
  view: N,
  query: Readonly<Record<string, string>> = {},
  options: UsePolledViewOptions = {},
): PolledView<WebViewRow<N>> {
  const enabled = options.enabled ?? true;
  const intervalMs = options.intervalMs ?? POLL_INTERVAL_MS;

  // The identity of `query` changes on every render for an inline object; its CONTENT is
  // what should drive a refetch. Serialising it is the cheapest honest key.
  const queryKey = JSON.stringify(query);
  const stableQuery = useMemo(() => JSON.parse(queryKey) as Record<string, string>, [queryKey]);

  const [rows, setRows] = useState<readonly WebViewRow<N>[] | null>(null);
  const [status, setStatus] = useState<ViewStatus>(enabled ? 'loading' : 'disabled');
  const [error, setError] = useState<string | null>(null);
  const [missing, setMissing] = useState(false);
  const [checkedAt, setCheckedAt] = useState<number | null>(null);
  const [counters, setCounters] = useState({ fetchCount: 0, notModifiedCount: 0 });

  /** Last body + ETag, so the next poll can be conditional. */
  const cache = useRef<Cached<WebViewRow<N>[]> | undefined>(undefined);
  /** Bumped to force an out-of-band poll. */
  const [nudge, setNudge] = useState(0);
  const refresh = useCallback(() => setNudge((value) => value + 1), []);

  useEffect(() => {
    // A change of view or filter invalidates the cached ETag: it validates a
    // representation, and this is a different representation.
    cache.current = undefined;
    setRows(null);
    setMissing(false);
    setCounters({ fetchCount: 0, notModifiedCount: 0 });
  }, [view, queryKey]);

  useEffect(() => {
    if (!enabled) {
      setStatus('disabled');
      return;
    }

    let cancelled = false;
    let timer: ReturnType<typeof setInterval> | null = null;

    // Focus and visibility both, because they are not the same event: switching desktops
    // fires visibilitychange, clicking back into the window fires focus.
    const onWake = (): void => {
      if (globalThis.document.visibilityState === 'visible') void poll();
    };
    const stop = (): void => {
      if (timer !== null) clearInterval(timer);
      timer = null;
      globalThis.removeEventListener('focus', onWake);
      globalThis.document.removeEventListener('visibilitychange', onWake);
    };

    const poll = async (): Promise<void> => {
      try {
        const result = await client.readView(view, stableQuery, cache.current);
        if (cancelled) return;
        cache.current = result;
        setRows(result.data);
        setError(null);
        setMissing(false);
        setStatus('ready');
        setCheckedAt(Date.now());
        setCounters((previous) =>
          result.notModified
            ? { ...previous, notModifiedCount: previous.notModifiedCount + 1 }
            : { ...previous, fetchCount: previous.fetchCount + 1 },
        );
      } catch (caught) {
        if (cancelled) return;
        // A 401 means the token died under us; the provider's expiry timer will sign out,
        // and until it does the page says so rather than showing stale rows as current.
        setError(caught instanceof ApiError ? describe(caught) : 'Could not reach the read plane.');
        setStatus('error');
        setCheckedAt(Date.now());
        // Not there is not a blip (see the file note): this view asks nothing more.
        const absent = caught instanceof ApiError && caught.status === 404;
        setMissing(absent);
        if (absent) stop();
      }
    };

    void poll();
    timer = setInterval(() => void poll(), intervalMs);
    globalThis.addEventListener('focus', onWake);
    globalThis.document.addEventListener('visibilitychange', onWake);

    return () => {
      cancelled = true;
      stop();
    };
  }, [client, view, stableQuery, enabled, intervalMs, nudge]);

  return { rows, status, error, missing, checkedAt, fetchCount: counters.fetchCount, notModifiedCount: counters.notModifiedCount, refresh };
}

function describe(error: ApiError): string {
  if (error.status === 401) return 'Your session is no longer accepted. Sign in again.';
  if (error.status === 404) return 'This view is not on this database yet (404) - the game server creates it the first time it runs against this Postgres.';
  if (error.status === 429) return 'Slow down - the edge is rate limiting this tab.';
  if (error.status === 502) return 'The read plane is not answering.';
  return `Read failed (${error.status}).`;
}
